/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import $ from 'jquery';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose, mapProps } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import connectToRouter from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';

import { typography2 } from '@coursera/cds-core';
import type { Partner } from '@coursera/grpc-types-educatoradmin/coursera/proto/educatoradmin/adminproductdirectory/v1beta1/partner';

import { ROUTE_ARGUMENTS } from 'bundles/admin-shared/analytics/constants';
import Naptime from 'bundles/naptimejs';
import UserPreferencesV1 from 'bundles/naptimejs/resources/userPreferences.v1';
import Selectize from 'bundles/phoenix/components/Selectize';

import _t from 'i18n!nls/admin-v2';

const styles = {
  partnerSelectionMenu: () => css`
    margin: var(--cds-spacing-150) var(--cds-spacing-150);
    top: 0;
    position: relative;
  `,
  selectize: () => css`
    select {
      ${typography2.bodyPrimary}
    }

    height: var(--cds-spacing-400);

    .selectize-control {
      ${typography2.bodyPrimary}
    }
  `,
};

type OptionInterface = {
  id: number;
  name: string;
  shortName: string;
};

type OuterProps = {
  selectedPartnerId?: number;
  partners: Partner[] | undefined;
  onBlur?: () => void;
  showSelectionMenu?: boolean;
};

type Props = OuterProps & {
  adminPartners: Array<OptionInterface>;
} & Naptime.AddedProps;

class PartnerSelectionMenu extends React.Component<Props> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof PartnerSelectionMenu.contextTypes>;

  handleSelect = (newPartnerId: string, item: HTMLDivElement) => {
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const partnerShortName = $(item)[0].dataset.shortname;
    const { naptime } = this.props;
    const { router } = this.context;
    const partnerId = parseInt(newPartnerId, 10);
    const key = UserPreferencesV1.keys.ADMIN_SELECTED_PARTNER;
    const oldPartnerShortName = router.params[ROUTE_ARGUMENTS.INSTITUTION_SLUG];

    naptime.executeMutation(UserPreferencesV1.updatePreference(key, { partnerId })).then(() => {
      const oldPath = redirect.getPathname();
      const newPath = oldPath.substring(oldPath.indexOf(oldPartnerShortName) + oldPartnerShortName.length + 1);
      redirect.setLocation(`/admin-v2/${partnerShortName}/${newPath}`);
    });
  };

  render() {
    const { adminPartners, selectedPartnerId, onBlur, showSelectionMenu, ...rest } = this.props;

    if (!selectedPartnerId || !showSelectionMenu) {
      return null;
    }

    const selectizeOptions = {
      persist: false,
      maxItems: 1,
      valueField: 'id',
      labelField: 'name',
      sortField: 'name',
      searchField: 'name',
      options: adminPartners,
      items: [selectedPartnerId],
      onItemAdd: this.handleSelect,
      createOnBlur: true,
      onBlur,
      render: {
        item: (data: OptionInterface, escape: (x: string) => void) => {
          return `<div data-shortName=${escape(data.shortName)}>${escape(data.name)}</div>`;
        },
      },
    };

    return (
      <div css={styles.partnerSelectionMenu}>
        <div css={styles.selectize}>
          <Selectize options={selectizeOptions} placeholder={_t('Select institution')} {...rest} focus />
        </div>
      </div>
    );
  }
}

/* This component uses selectize which only works on CSR. Don't render until mount. */
export default compose<Props, OuterProps>(
  deferToClientSideRender(),
  connectToRouter((router) => {
    return { shortName: router.params[ROUTE_ARGUMENTS.INSTITUTION_SLUG] };
  }),
  Naptime.createContainer((props) => props),
  mapProps(
    ({ partners, ...rest }: { partners?: Array<{ partnerName: string; shortName: string; partnerId: string }> }) => {
      const partnerOptions = _.map(partners, ({ partnerId, partnerName, shortName }) => ({
        id: parseInt(partnerId, 10),
        name: partnerName,
        shortName,
      }));

      return {
        adminPartners: partnerOptions,
        ...rest,
      };
    }
  )
)(PartnerSelectionMenu);
